import { Text } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ReactNode } from "react";
import {
  RESERVED_KEYS,
  getDataset,
  getDatasetStructure,
  type DatasetLabel,
  type DatasetStructure,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatLabelChip, sortLabelsForDisplay } from "@/components/label";
import {
  EpisodesViewer,
  datasetFileBrowserSource,
  formatDatasetLabel,
  resolveLoadErrorMessage,
  type LoadState,
} from "@/components/dataset";
import { ChannelStructureGroups } from "@/components/dataset/ChannelStructureGroups";
import { MemoStrip } from "@/components/memo";
import { CollapsibleSection } from "@/components/browse/CollapsibleSection";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { DatasetSelection } from "./DatasetSelection";
import styles from "./DatasetSelection.module.css";
import { formatArmLayout } from "@/components/filter/datasetFilters";

// 선택 Dataset 의 필수 라벨(작업·환경·출처)을 1회 fetch 한다. 펼친 상세가 칩으로 보여 준다.
// 확정된 라벨만 오므로 비어 있을 수 있다.
function useDatasetReservedLabels(selection: DatasetSelection): LoadState<readonly DatasetLabel[]> {
  const [state, setState] = useState<LoadState<readonly DatasetLabel[]>>({ status: "idle" });
  const { uuid } = selection;
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getDataset(uuid)
      .then((dataset) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "success",
          data: sortLabelsForDisplay(
            dataset.labels.filter((label) => RESERVED_KEYS.includes(label.key)),
          ),
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);
  return state;
}

// 선택 Dataset 의 채널 구조를 1회 fetch 한다. 한팔/양팔 뱃지와 topic 별 채널 칩에 쓴다.
function useDatasetStructure(selection: DatasetSelection): LoadState<DatasetStructure> {
  const [state, setState] = useState<LoadState<DatasetStructure>>({ status: "idle" });
  const { uuid } = selection;
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getDatasetStructure(uuid)
      .then((structure) => {
        if (!cancelled) {
          setState({ status: "success", data: structure });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);
  return state;
}

// 접힌 헤더 요약. 팔 구성과 에피소드 수를 표시해 접힌 상태에서도 규모를 알 수 있게 한다.
function structureSummaryLabel(structure: DatasetStructure): string {
  const armLabel = formatArmLayout(structure.arm_layout) ?? "미판정";
  return `${armLabel} · ${structure.episode_count} episodes`;
}

// 필수 라벨 칩 목록. 목록 표·카드와 같은 표기(formatDatasetLabel + 가이드 툴팁)를 쓴다.
function DatasetReservedLabelChips({ labels }: { labels: readonly DatasetLabel[] }): ReactNode {
  if (labels.length === 0) {
    return <EmptyState size="sm">확정된 필수 라벨이 없습니다.</EmptyState>;
  }
  return (
    <div className={styles.previewLabelList} aria-label="필수 라벨">
      {labels.map((label) => (
        <HatLabelChip
          key={label.uuid}
          labelKey={label.key}
          value={label.value}
          color={label.color}
          description={label.description}
        >
          {formatDatasetLabel(label)}
        </HatLabelChip>
      ))}
    </div>
  );
}

// 접기 헤더 제목. 데이터셋 이름(굵게)과 보기/닫기 힌트다. 이름은 여기에만 둔다.
function previewTitle(name: string, open: boolean): ReactNode {
  return (
    <span className={styles.previewTitle}>
      <Text variant={hatcheryTextModes["body-bold"]} className={styles.previewTitleName}>
        {name}
      </Text>
      <span className={styles.previewTitleHint}>{open ? "상세 닫기" : "상세 보기"}</span>
    </span>
  );
}

// 우측 미리보기 패널.
//   - 상단: 접힌 "데이터셋 상세". 헤더는 이름과 팔 구성·에피소드 요약이다. 본문은 메모,
//     필수 라벨 칩, 채널 구조다.
//   - 하단: 에피소드 목록과 영상.
// 상세는 기본으로 접혀 있으므로 뷰어가 넓은 영역을 쓴다. 학습 입력은 공용 Dataset 뿐이므로
// detail, viewer, memo 의 대상도 모두 공용 Dataset 축이다.
export function DatasetPreviewPanel({ selection }: { selection: DatasetSelection }): ReactNode {
  const reservedLabels = useDatasetReservedLabels(selection);
  const structure = useDatasetStructure(selection);
  return (
    <div className={styles.previewPanel}>
      <div className={styles.previewInfo}>
        <CollapsibleSection
          title={previewTitle(selection.label, false)}
          openTitle={previewTitle(selection.label, true)}
          summary={
            structure.status === "success" ? structureSummaryLabel(structure.data) : undefined
          }
        >
          <MemoStrip datasetUuid={selection.uuid} />
          {reservedLabels.status === "error" ? (
            <EmptyState size="sm" tone="error">
              {reservedLabels.message}
            </EmptyState>
          ) : reservedLabels.status === "success" ? (
            <DatasetReservedLabelChips labels={reservedLabels.data} />
          ) : null}
          {structure.status === "success" ? (
            structure.data.channels.length === 0 ? (
              <EmptyState size="sm">채널 구조 정보가 없습니다.</EmptyState>
            ) : (
              <ChannelStructureGroups
                mode="static"
                channels={structure.data.channels}
                episodeCount={structure.data.episode_count}
              />
            )
          ) : structure.status === "error" ? (
            <EmptyState size="sm" tone="error">
              채널 구조를 불러오지 못했습니다.
            </EmptyState>
          ) : (
            <LoadingState size="sm">채널 구조를 불러오고 있습니다.</LoadingState>
          )}
        </CollapsibleSection>
      </div>
      <div className={styles.previewViewer}>
        {/* 선택이 바뀌면 상위 SubmitWizardSteps 의 UUID key 가 remount 를 일으켜 뷰어 상태를 격리한다. */}
        <EpisodesViewer source={datasetFileBrowserSource} resourceUuid={selection.uuid} />
      </div>
    </div>
  );
}
